import React, { useState, useEffect } from 'react';
import Card from '../../../ui/Card';
import { TableContainer, Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../../../ui/Table/Table';
import Button from '../../../ui/Button';
import IconButton from '../../../ui/IconButton';
import { CellText, CellId, CellTag, CellMuted } from '../../../ui/DataTable/cells';
import Tabs from '../../../ui/Tabs/Tabs';
import EmptyState from '../../../ui/EmptyState';
import { Field, Input, Textarea, Select } from '../../../ui/Field/Field';
import { toast, confirmDialog } from '../../../ui/feedback';
import Icon from '../../../ui/Icon';
import { ICONS } from '../../../ui/icon-map';
import { kpiService } from '../../../services/kpiService';
import { fleetService } from '../../../services/firestore';
import { modelPricingService } from '../../../services/modelPricingService';
import logger from '../../../utils/logger';
import { isGone } from '../../../services/fleetStatus';
import { usePermissions } from '../../../hooks/usePermissions';

// No targets and no fixed branch per employee any more: everyone who opens a contract in Ejar appears on
// the dashboard by themselves, in the branch where they worked. Settings keep two things: the English
// display name for an Ejar (Arabic) name, and the model prices used for revenue.
const KPISettings = ({ staffMappingsProp = {}, onSave }) => {
    const [isSaving, setIsSaving] = useState(false);
    // the prices and the staff names: each its own permission (permissions phase 4, 4 Oct 2026); a role with only the
    // prices opens on them
    const { hasPermission } = usePermissions();
    const canPrices = hasPermission('kpi_edit_pricing');
    const canNames = hasPermission('kpi_edit_staff_names');
    const [activeTab, setActiveTab] = useState(() => (!canNames && canPrices ? 'models' : 'staff')); // 'staff' | 'models'
    const [modelPrices, setModelPrices] = useState({});
    const [isScanning, setIsScanning] = useState(false);
    const [staffMappings, setStaffMappings] = useState(staffMappingsProp || {});
    const [staffNos, setStaffNos] = useState({});
    const [rawStaffNames, setRawStaffNames] = useState([]);
    const [newMapping, setNewMapping] = useState({ displayName: '', realNames: '', staffNo: '' });
    const [excluded, setExcluded] = useState([]);
    const [toExclude, setToExclude] = useState('');

    useEffect(() => {
        modelPricingService.getAllPricing().then((p) => setModelPrices(p || {}));
        kpiService.getStaffMappings().then((m) => setStaffMappings(m || {}));
        kpiService.getRawStaffNames().then((n) => setRawStaffNames(n || []));
        kpiService.getStaffNos().then((n) => setStaffNos(n || {}));
        kpiService.getStaffExcluded().then((n) => setExcluded(n || []));
    }, [staffMappingsProp]);

    // --- Model Pricing Handlers ---
    const handleScanModels = async () => {
        setIsScanning(true);
        try {
            const cars = await fleetService.getAllCars();
            // Create unique keys: "MODEL - YEAR" (sold / stolen / Ready To Sell cars left out: the fleet's one GONE rule)
            const uniqueKeys = new Set();
            cars.forEach(c => {
                if (isGone(c.status)) return;
                const model = (c.model || c.Model || '').trim().toUpperCase();
                const year = (c.year || c.Year || '').toString().trim();
                if (model) uniqueKeys.add(`${model} - ${year || 'N/A'}`);
            });

            const sortedKeys = Array.from(uniqueKeys).sort();
            const added = sortedKeys.filter((k) => !modelPrices[k]);
            setModelPrices(prev => {
                const next = { ...prev };
                added.forEach((key) => { next[key] = { daily: 0, monthly: 0 }; });
                return next;
            });
            toast.success(added.length
                ? `Found ${sortedKeys.length} models in the fleet; ${added.length} have no price yet: ${added.join(', ')}.`
                : `All ${sortedKeys.length} models in the fleet have a price.`);
        } catch (error) {
            logger.error('Error scanning models:', error);
            toast.error('Failed to scan models.');
        } finally {
            setIsScanning(false);
        }
    };

    const handleModelPriceChange = (model, field, value) => {
        setModelPrices(prev => ({ ...prev, [model]: { ...prev[model], [field]: Number(value) } }));
    };

    const handleSaveModel = async (model) => {
        const p = modelPrices[model];
        await modelPricingService.savePricing(model, p.daily, p.monthly);
        toast.success(`Saved pricing for ${model}`);
    };

    const handleDeleteModel = async (model) => {
        if (await confirmDialog({ title: 'Delete pricing?', message: `Delete pricing for ${model}?`, confirmLabel: 'Delete', danger: true })) {
            await modelPricingService.deletePricing(model);
            setModelPrices(prev => {
                const next = { ...prev };
                delete next[model];
                return next;
            });
        }
    };

    const handleSaveAllModels = async () => {
        setIsSaving(true);
        try {
            const result = await modelPricingService.saveAllPricing(modelPrices);
            if (result.success) {
                toast.success('All model pricing saved successfully!');
                onSave();
            } else {
                toast.error('Error saving model pricing: ' + result.error);
            }
        } catch (error) {
            logger.error('Failed to save all model pricing:', error);
            toast.error('A critical error occurred while saving.');
        } finally {
            setIsSaving(false);
        }
    };

    // --- Staff Name Handlers ---
    const handleAddMapping = () => {
        const name = newMapping.displayName.trim();
        if (!name) return;
        const realNamesArr = newMapping.realNames.split(',').map(n => n.trim()).filter(Boolean);
        if (realNamesArr.length === 0) return;
        setStaffMappings(prev => ({ ...prev, [name]: realNamesArr }));
        if (newMapping.staffNo.trim()) setStaffNos(prev => ({ ...prev, [name]: newMapping.staffNo.trim() }));
        setNewMapping({ displayName: '', realNames: '', staffNo: '' });
    };

    const handleDeleteMapping = (displayName) => {
        setStaffMappings(prev => {
            const next = { ...prev };
            delete next[displayName];
            return next;
        });
        setStaffNos(prev => {
            const next = { ...prev };
            delete next[displayName];
            return next;
        });
    };

    const handleSaveStaffMappings = async () => {
        setIsSaving(true);
        try {
            const { last_updated: lu1, ...cleanMappings } = staffMappings;
            const { last_updated: lu2, ...cleanNos } = staffNos;
            const [r1, r2, r3] = await Promise.all([
                kpiService.saveStaffMappings(cleanMappings),
                kpiService.saveStaffNos(cleanNos),
                kpiService.saveStaffExcluded(excluded),
            ]);
            if (r1.success && r2.success && r3.success) {
                toast.success('Staff names saved.');
                onSave();
            } else {
                toast.error('Error saving data: ' + (r1.error || r2.error || r3.error));
            }
        } catch (error) {
            logger.error('Failed to save staff data:', error);
            toast.error('A critical error occurred.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleAddRawName = (name) => {
        setNewMapping(prev => ({ ...prev, realNames: prev.realNames ? `${prev.realNames}, ${name}` : name }));
    };

    const allMappedNames = Object.entries(staffMappings).filter(([k]) => k !== 'last_updated').flatMap(([, v]) => (Array.isArray(v) ? v : []));
    const unmappedNames = rawStaffNames.filter(name => !allMappedNames.includes(name));
    const staffEntries = Object.entries(staffMappings).filter(([key]) => key !== 'last_updated');
    // A name to leave out: a display name, or an Ejar name with no display name
    const excludable = Array.from(new Set([...staffEntries.map(([k]) => k), ...unmappedNames]))
        .filter((n) => !excluded.includes(n)).sort();
    const addExcluded = () => {
        if (!toExclude) return;
        setExcluded((prev) => Array.from(new Set([...prev, toExclude])));
        setToExclude('');
    };
    const saveIcon = <Icon glyph={ICONS.save} size="sm" />;

    return (
        <Card>
            <div className="flex flex-col gap-3 mb-4">
                <h2 className="ds-section-title flex items-center gap-2">
                    <Icon glyph={ICONS.settings} size="sm" tone="muted" />
                    KPI Settings
                </h2>
                <Tabs
                    items={[
                        { id: 'staff', label: 'Staff Names', icon: <Icon glyph={ICONS.users} size="sm" /> },
                        { id: 'models', label: 'Model Pricing', icon: <Icon glyph={ICONS.car} size="sm" /> },
                    ]}
                    value={activeTab}
                    onChange={setActiveTab}
                    label="KPI settings sections"
                    idPrefix="kpi-settings"
                />
            </div>

            <p className="text-xs text-gray-500 mb-6 border-l-2 border-gray-200 pl-3">
                Everyone who opens a contract in Ejar appears on the dashboard by themselves, in the branch where they worked.
                There are no targets: each employee is read against the team average and their own previous period.
                Here you only give an Ejar name the English name to show.
            </p>

            {activeTab === 'models' && (
                <div role="tabpanel" id="kpi-settings-panel-models" aria-labelledby="kpi-settings-models">
                    <div className="flex flex-wrap gap-3 justify-between items-center mb-4">
                        <div>
                            <h3 className="text-sm font-semibold text-gray-900">Model Standard Pricing</h3>
                            <p className="text-xs text-gray-500">Contracts carry no rate, so revenue comes from these prices (model + year).</p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <Button
                                variant="secondary"
                                onClick={handleScanModels}
                                isLoading={isScanning}
                                leadingIcon={<Icon glyph={ICONS.search} size="sm" />}
                            >
                                {isScanning ? 'Scanning...' : 'Scan Fleet for Models'}
                            </Button>
                            {canPrices && <Button
                                variant="primary"
                                onClick={handleSaveAllModels}
                                isLoading={isSaving}
                                leadingIcon={saveIcon}
                            >
                                {isSaving ? 'Saving Changes...' : 'Save All Changes'}
                            </Button>}
                        </div>
                    </div>

                    <TableContainer maxHeight={600} className="overflow-auto">
                        <Table sticky>
                            <TableHead>
                                <TableRow>
                                    <TableHeader>Car Model - Year</TableHeader>
                                    <TableHeader>Stand. Daily Rate (AED)</TableHeader>
                                    <TableHeader>Stand. Monthly Rate (AED)</TableHeader>
                                    <TableHeader>Actions</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {Object.keys(modelPrices).sort().map(modelKey => (
                                    <TableRow key={modelKey}>
                                        <TableCell>
                                            <CellText>{modelKey}</CellText>
                                            {!modelPrices[modelKey]?.daily && !modelPrices[modelKey]?.monthly && (
                                                <span className="ml-2"><CellTag tone="warning">No price</CellTag></span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Input
                                                type="number"
                                                size="sm"
                                                aria-label={`Daily rate for ${modelKey}`}
                                                value={modelPrices[modelKey]?.daily || 0}
                                                onChange={e => handleModelPriceChange(modelKey, 'daily', e.target.value)}
                                                className="w-28 mx-auto"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Input
                                                type="number"
                                                size="sm"
                                                aria-label={`Monthly rate for ${modelKey}`}
                                                value={modelPrices[modelKey]?.monthly || 0}
                                                onChange={e => handleModelPriceChange(modelKey, 'monthly', e.target.value)}
                                                className="w-28 mx-auto"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {canPrices && <div className="flex justify-center gap-2">
                                                <Button size="sm" variant="secondary" onClick={() => handleSaveModel(modelKey)}>Save</Button>
                                                <Button size="sm" variant="danger" onClick={() => handleDeleteModel(modelKey)}>Delete</Button>
                                            </div>}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        {Object.keys(modelPrices).length === 0 && (
                            <EmptyState icon={ICONS.car} title="No models found" description='Click "Scan Fleet" to populate.' />
                        )}
                    </TableContainer>
                </div>
            )}
            {activeTab === 'staff' && (
                <div role="tabpanel" id="kpi-settings-panel-staff" aria-labelledby="kpi-settings-staff">
                    <div className="flex flex-wrap gap-3 justify-between items-center mb-4">
                        <div>
                            <h3 className="text-sm font-semibold text-gray-900">Staff Names</h3>
                            <p className="text-xs text-gray-500">The English name to show for a name in Ejar. A name with no entry here is shown as Ejar has it.</p>
                        </div>
                        {canNames && <Button
                            variant="primary"
                            onClick={handleSaveStaffMappings}
                            isLoading={isSaving}
                            leadingIcon={saveIcon}
                        >
                            {isSaving ? 'Saving Changes...' : 'Save Names'}
                        </Button>}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                        <div className="md:col-span-1 space-y-6">
                            <Card padding="sm" title="Add a name">
                                <div className="space-y-3">
                                    <Field label="Display Name (e.g. Joy)">
                                        <Input
                                            value={newMapping.displayName}
                                            onChange={e => setNewMapping({ ...newMapping, displayName: e.target.value })}
                                            placeholder="Display Name"
                                        />
                                    </Field>
                                    <Field label="Names in Ejar (comma separated)">
                                        <Textarea
                                            value={newMapping.realNames}
                                            onChange={e => setNewMapping({ ...newMapping, realNames: e.target.value })}
                                            placeholder="Name 1, Name 2, ..."
                                        />
                                    </Field>
                                    <Field label="Staff No (optional)">
                                        <Input
                                            value={newMapping.staffNo}
                                            onChange={e => setNewMapping({ ...newMapping, staffNo: e.target.value })}
                                            placeholder="Staff ID / Number"
                                        />
                                    </Field>
                                    <div className="pt-2">
                                        <Button variant="secondary" fullWidth onClick={handleAddMapping} leadingIcon={<Icon glyph={ICONS.plus} size="sm" />}>
                                            Add to List
                                        </Button>
                                    </div>
                                </div>
                            </Card>

                            <Card padding="sm" title="Not counted as employees" subtitle="People who left or do not open contracts. Their contracts count for the branch they were opened in.">
                                <div className="space-y-3">
                                    <div className="flex flex-wrap gap-1">
                                        {excluded.length > 0 ? excluded.map((n) => (
                                            <span key={n} className="inline-flex items-center gap-1">
                                                <CellTag tone="warning">{n}</CellTag>
                                                <IconButton icon={ICONS.close} label={`Count ${n} as an employee again`} size="sm"
                                                    onClick={() => setExcluded((prev) => prev.filter((x) => x !== n))} />
                                            </span>
                                        )) : <CellMuted>Everyone is counted.</CellMuted>}
                                    </div>
                                    <Field label="Leave out">
                                        <Select value={toExclude} onChange={(e) => setToExclude(e.target.value)}>
                                            <option value="">Choose a name…</option>
                                            {excludable.map((n) => <option key={n} value={n}>{n}</option>)}
                                        </Select>
                                    </Field>
                                    <Button variant="secondary" fullWidth onClick={addExcluded} disabled={!toExclude}>Leave out</Button>
                                    <CellMuted>Press Save Names to keep the change.</CellMuted>
                                </div>
                            </Card>

                            <Card padding="sm" title="Names in Ejar with no English name" subtitle="Click one to add it to the form.">
                                <div className="flex flex-wrap gap-1 max-h-[250px] overflow-y-auto pr-1">
                                    {unmappedNames.length > 0 ? unmappedNames.map(name => (
                                        <Button key={name} size="xs" variant="secondary" onClick={() => handleAddRawName(name)}>
                                            {name}
                                        </Button>
                                    )) : <p className="text-xs text-green-700 font-medium">Every name has an English name.</p>}
                                </div>
                            </Card>
                        </div>

                        <TableContainer maxHeight={600} className="md:col-span-3 overflow-auto">
                            <Table sticky>
                                <TableHead>
                                    <TableRow>
                                        <TableHeader>Display Name</TableHeader>
                                        <TableHeader>Staff No</TableHeader>
                                        <TableHeader>Names in Ejar</TableHeader>
                                        <TableHeader className="w-20">Actions</TableHeader>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {staffEntries.map(([displayName, realNames]) => (
                                        <TableRow key={displayName}>
                                            <TableCell><CellText>{displayName}</CellText></TableCell>
                                            <TableCell><CellId>{staffNos[displayName]}</CellId></TableCell>
                                            <TableCell>
                                                <div className="flex flex-wrap justify-center gap-1">
                                                    {Array.isArray(realNames) ? realNames.map(rn => (
                                                        <CellTag key={rn} tone="neutral">{rn}</CellTag>
                                                    )) : <CellMuted>No names</CellMuted>}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <IconButton
                                                    icon={ICONS.delete}
                                                    label={`Remove the name ${displayName}`}
                                                    size="sm"
                                                    onClick={() => handleDeleteMapping(displayName)}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            {staffEntries.length === 0 && (
                                <EmptyState icon={ICONS.users} title="No names yet" description="Use the form on the left to add one." />
                            )}
                        </TableContainer>
                    </div>
                </div>
            )}
        </Card>
    );
};

export default KPISettings;
